# Terms of Service

**Partiri — partiri.cloud**

---

Welcome to Partiri. Please read the following Terms of Service ("Terms") carefully before using our website located at partiri.cloud and its subdomains (collectively, the "Website"), as well as the products, services, cloud computing platform, APIs, and all other services provided on or in connection with the platform (collectively, the "Services"). These Terms constitute a binding agreement between you or the entity you represent ("Customer," "you," or "your") and Partiri ("Partiri," "we," "us," or "our").

By creating an account, clicking "I Agree," or otherwise accessing or using the Services, you confirm that you have read, understood, and agree to be bound by these Terms, our [Privacy Policy], our [Acceptable Use Policy], and our [Data Processing Agreement]. If you do not agree to these Terms, you may not access or use the Services.

If you are entering into these Terms on behalf of an entity, you represent and warrant that you have the authority to bind that entity. In such a case, "you" and "your" refer to that entity.

---

## 1. Eligibility

You may use the Services only if you are at least 18 years of age and capable of entering into a legally binding contract under applicable law. By using the Services, you represent and warrant that you meet these requirements. Any access or use of the Services by anyone under 18 is strictly prohibited. The Services are not available to any person previously removed from the Services by us.

## 2. User Accounts

### 2.1 Registration

To access certain features of the Services, you must create an account. When registering, you must provide truthful, accurate, and complete information and keep that information up to date. You may not select a username that impersonates another person or entity, or that you do not have the right to use.

### 2.2 Account Security

You are solely responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You must use strong, unique passwords and notify Partiri immediately at legal@partiri.cloud if you become aware of any unauthorised use of your account or any other security breach. Partiri shall not be liable for any loss arising from unauthorised use of your account.

### 2.3 Accounts on Behalf of Organisations

If you open an account on behalf of a company, organisation, or other entity, you represent and warrant that you are an authorised representative with the authority to bind that entity to these Terms.

### 2.4 Administrator-Assigned Accounts

In some cases, an account may be assigned to you by an administrator (e.g. your employer or educational institution). Additional terms may apply to your use of such accounts, and your administrator may be able to access, restrict, or terminate your account.

## 3. The Services

### 3.1 Subscription and Access

Subject to the terms of this agreement and payment of applicable fees, Partiri grants you a non-exclusive, non-transferable, non-sublicensable, revocable right to access and use the Services during the applicable subscription term, solely for your own internal purposes and in compliance with all applicable laws and regulations.

### 3.2 Authorised Users

You may permit your employees, agents, and contractors ("Authorised Users") to access and use the Services under your account in accordance with these Terms. You are responsible for all acts and omissions of your Authorised Users.

### 3.3 Restrictions

You shall not, and shall not permit any Authorised User or third party to:

- reverse engineer, disassemble, or decompile any component of the Services;
- interfere with or disrupt the integrity, performance, or security of the Services;
- sublicense any of your rights under these Terms or use the Services for the benefit of a third party other than as permitted;
- modify, copy, or create derivative works based on any part of the Services;
- use the Services in a manner that violates any applicable law, infringes on anyone's rights, or exceeds the scope of use permitted under these Terms;
- attempt to gain unauthorised access to the Services or related systems or networks;
- run any automated processes (such as scraping or crawling) against the Services without prior written consent; or
- use the Services to transmit malware, viruses, or other harmful code.

### 3.4 Changes to the Services

We may, without prior notice, modify, suspend, or discontinue the Services or any features thereof at any time. We may also introduce new features, impose limits on existing features, or restrict access to parts or all of the Services. Where reasonably practicable, we will endeavour to provide advance notice of material changes.

### 3.5 Support

Partiri shall provide support services. Partiri will use commercially reasonable efforts to ensure the availability and quality of such support.

## 4. Customer Content and Data

### 4.1 Ownership

You retain all rights, title, and interest in and to any data, content, or materials you upload, submit, or otherwise make available through the Services ("Customer Data"), including all intellectual property rights therein. Partiri does not claim ownership of your Customer Data.

### 4.2 Licence to Partiri

By using the Services, you grant Partiri a non-exclusive, worldwide, royalty-free licence to use, reproduce, store, and process your Customer Data solely as necessary to provide, maintain, and improve the Services. All rights in your Customer Data not expressly granted to Partiri are reserved by you.

### 4.3 Customer Warranties

You represent and warrant that your Customer Data and your use of the Services shall not:

- infringe, misappropriate, or violate any intellectual property rights, privacy rights, or other rights of any third party;
- contain any content that is unlawful, defamatory, obscene, or otherwise harmful;
- contain viruses, worms, or other malicious code; or
- otherwise violate any applicable law or regulation.

### 4.4 Responsibility for Data and Backups

You are solely responsible for the accuracy, quality, integrity, legality, and security of your Customer Data. Partiri is not obligated to back up Customer Data. You are responsible for maintaining your own backup copies of Customer Data at your own cost. Partiri recommends that you regularly export and back up your data.

### 4.5 Feedback

If you submit comments, ideas, suggestions, or other feedback relating to the Services ("Feedback"), you grant Partiri a royalty-free, worldwide, transferable, sublicensable, irrevocable, perpetual licence to use and incorporate such Feedback into the Services without any obligation or compensation to you. Partiri shall not identify you as the source of any Feedback without your prior written consent.

## 5. Proprietary Rights

### 5.1 Partiri Technology

The Services, including all software, documentation, designs, text, graphics, logos, and other materials provided by Partiri (the "Partiri Technology"), and all intellectual property rights therein, are and remain the exclusive property of Partiri or its licensors. Nothing in these Terms grants you any right, title, or interest in the Partiri Technology except for the limited use rights expressly set out herein.

### 5.2 No Unauthorised Use

You may not use, copy, reproduce, modify, distribute, sell, license, or create derivative works from any Partiri Technology unless expressly permitted by these Terms or with Partiri's prior written consent.

## 6. Fees and Payment

### 6.1 Fees

In consideration for access to the Services, you agree to pay all applicable fees ("Fees") as set out at partiri.cloud/pricing or as otherwise agreed in writing. All Fees are stated exclusive of applicable taxes unless otherwise indicated.

### 6.2 Taxes

You are responsible for all applicable taxes (including VAT), duties, and government assessments arising in connection with your use of the Services, except for taxes based on Partiri's net income. If Partiri is required to collect VAT or other indirect taxes, such taxes will be added to your invoice.

### 6.3 Payment Terms

Fees are billed in advance on a monthly or annual basis, as selected during registration.

### 6.4 Late Payment

Partiri reserves the right to suspend or restrict access to the Services if Fees remain unpaid.

### 6.6 Refunds

Except as required by applicable law, Fees are non-refundable. Upon termination of your account, no refund will be issued for any unused portion of a subscription period.

### 6.7 Payment Processor

We use a third-party payment processor to handle payments. By using the Services, you agree to the applicable terms and conditions of the payment processor. Partiri is not responsible for errors or omissions by the payment processor.

## 7. Confidentiality

### 7.1 Definition

"Confidential Information" means any non-public information disclosed by one party (the "Disclosing Party") to the other party (the "Receiving Party"), whether in written, oral, or digital form, that is identified as confidential or that the Receiving Party knows or should reasonably know is confidential. The Services and all Partiri Technology shall be considered Confidential Information of Partiri.

### 7.2 Obligations

The Receiving Party shall not use or disclose any Confidential Information except as expressly permitted by these Terms. The Receiving Party shall protect the Disclosing Party's Confidential Information using at least the same degree of care it applies to its own confidential information, but no less than reasonable care.

### 7.3 Exceptions

Confidential Information does not include information that: (a) is or becomes publicly available without breach of these Terms; (b) was already known by the Receiving Party without obligation of confidentiality; (c) is independently developed by the Receiving Party without access to the Disclosing Party's Confidential Information; or (d) is lawfully received from a third party without restriction.

### 7.4 Required Disclosure

If the Receiving Party is compelled by law to disclose Confidential Information, it shall, to the extent legally permitted, notify the Disclosing Party in advance and cooperate to seek appropriate protective measures.

### 7.5 Usage Data

Partiri may collect anonymised and aggregated usage and performance data relating to the Services ("Usage Data"). Partiri may use Usage Data to provide, improve, and market its products and services and to disclose such data in an aggregated and de-identified manner.

## 8. Data Protection and Privacy

### 8.1 Privacy Policy

The Services are provided in accordance with our Privacy Policy, available at partiri.cloud/privacy. The Privacy Policy describes how we collect, use, and share personal data.

### 8.2 Data Processing Agreement

To the extent that Partiri processes personal data on your behalf within the meaning of the General Data Protection Regulation (EU) 2016/679 ("GDPR").

### 8.3 GDPR Compliance

Both parties shall comply with all applicable data protection laws, including the GDPR and the German Federal Data Protection Act (BDSG). You are responsible for ensuring that your use of the Services complies with all applicable data protection requirements, including obtaining any necessary consents from data subjects.

## 9. Term and Termination

### 9.1 Term

These Terms commence on the date you first access or use the Services and shall remain in effect until terminated by either party.

### 9.2 Termination by You

You may terminate your account at any time through your account settings or by contacting us at support@partiri.cloud. Termination takes effect at the end of the current subscription period.

### 9.3 Termination by Partiri

Partiri may terminate or suspend your access to the Services immediately upon written notice if you breach any provision of these Terms. Partiri may also terminate these Terms for convenience upon thirty (30) days' written notice, effective at the end of the then-current subscription term.

### 9.4 Effect of Termination

Upon termination or expiration:

- all rights and licences granted to you under these Terms shall immediately cease;
- any amounts owed to Partiri become immediately due and payable;
- you must cease all use of the Services and delete any copies of Partiri Technology in your possession;
- Partiri will, upon request, make your Customer Data available for export for a period of thirty (30) days following the effective date of termination, after which Partiri may delete your Customer Data in accordance with its data retention policies and applicable law.

### 9.5 Survival

The following sections shall survive termination of these Terms: Sections 4 (Customer Content and Data), 5 (Proprietary Rights), 6 (Fees and Payment, to the extent of amounts owed), 7 (Confidentiality), 8 (Data Protection and Privacy), 10 (Disclaimer of Warranties), 11 (Limitation of Liability), 12 (Indemnification), and 14 (General Provisions).

## 10. Disclaimer of Warranties

To the maximum extent permitted by applicable law, the Services and all associated documentation are provided "as is" and "as available" without warranties of any kind, whether express, implied, or statutory. Partiri disclaims all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.

Partiri does not warrant that the Services will be uninterrupted, error-free, or secure, or that any defects will be corrected. No advice or information, whether oral or written, obtained from Partiri or through the Services shall create any warranty not expressly stated herein.

**This disclaimer does not affect any mandatory statutory warranty rights you may have under German or European Union law, including but not limited to those under §§ 434 ff. BGB.**

## 11. Limitation of Liability

### 11.1 Exclusion of Indirect Damages

To the maximum extent permitted by applicable law, Partiri and its affiliates, suppliers, and licensors shall not be liable for any indirect, incidental, special, consequential, or exemplary damages, including but not limited to loss of profits, loss of data, business interruption, or loss of goodwill, arising out of or in connection with the Services or these Terms, regardless of the legal theory and even if Partiri has been advised of the possibility of such damages.

### 11.2 Cap on Liability

Partiri's total aggregate liability under or in connection with these Terms shall not exceed the total Fees paid by you to Partiri during the twelve (12) months immediately preceding the event giving rise to the claim.

## 12. Indemnification

You agree to defend, indemnify, and hold harmless Partiri and its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including reasonable legal fees) arising from:

- your use of or access to the Services;
- your violation of any term of these Terms;
- your violation of any third-party right, including intellectual property, privacy, or other proprietary rights;
- your violation of any applicable law or regulation; or
- your Customer Data.

## 13. Intellectual Property Infringement

### 13.1 Reporting Infringement

It is Partiri's policy to respond to notices of alleged intellectual property infringement. If you believe that any content on the Services infringes your intellectual property rights, please submit a written notice to legal@partiri.cloud containing:

- a description of the work you claim has been infringed;
- a description of the allegedly infringing material and its location on the Services;
- your contact information (name, address, email, and telephone number);
- a statement that you have a good-faith belief that the use is not authorised by the rights holder, its agent, or the law; and
- a statement, made under penalty of perjury, that the information in the notice is accurate and that you are the rights holder or authorised to act on their behalf.

### 13.2 Repeat Infringers

Partiri reserves the right to terminate the accounts of users who are found to be repeat infringers.

## 14. General Provisions

### 14.1 Governing Law

These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Germany, excluding the United Nations Convention on Contracts for the International Sale of Goods (CISG) and any conflict-of-laws provisions that would require the application of the law of another jurisdiction.

### 14.2 Jurisdiction

The exclusive place of jurisdiction for all disputes arising out of or in connection with these Terms shall be the courts of Berlin, Germany, unless mandatory statutory provisions require a different jurisdiction. If you are a consumer within the meaning of the German Civil Code (BGB), the statutory rules on jurisdiction apply.

### 14.3 Dispute Resolution

In the event of any dispute arising out of or in connection with these Terms, the parties shall first attempt to resolve the dispute amicably through good-faith negotiation. If the dispute cannot be resolved within thirty (30) days, either party may pursue legal remedies.

### 14.4 European Online Dispute Resolution

In accordance with Regulation (EU) No. 524/2013, the European Commission provides an Online Dispute Resolution (ODR) platform, accessible at [https://ec.europa.eu/consumers/odr](https://ec.europa.eu/consumers/odr). Partiri is not obligated and currently not willing to participate in dispute resolution proceedings before a consumer arbitration board.

### 14.5 Assignment

You may not assign, transfer, or delegate these Terms or any of your rights or obligations hereunder without Partiri's prior written consent. Partiri may assign these Terms freely, including in connection with a merger, acquisition, reorganisation, or sale of substantially all of its assets.

### 14.6 Severability

If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in effect. The invalid or unenforceable provision shall be replaced with a valid and enforceable provision that most closely reflects the original intent.

### 14.7 No Waiver

The failure of Partiri to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. Any waiver must be in writing and signed by an authorised representative of Partiri.

### 14.8 Force Majeure

Partiri shall not be liable for any failure or delay in the performance of its obligations caused by events beyond its reasonable control, including but not limited to natural disasters, acts of war or terrorism, pandemics, strikes, government actions, power failures, or internet disruptions.

### 14.9 Independent Contractors

The relationship between you and Partiri is that of independent contractors. Nothing in these Terms shall be construed as creating a partnership, joint venture, employment, or agency relationship.

### 14.10 Notices

All notices under these Terms shall be in writing. Notices to Partiri must be sent to legal@partiri.cloud. Notices to you will be sent to the email address associated with your account. Either party may update its notice address by written notice to the other party.

### 14.11 Entire Agreement

These Terms, together with the Privacy Policy, Acceptable Use Policy, Data Processing Agreement, and any other documents incorporated by reference, constitute the entire agreement between you and Partiri concerning the Services and supersede all prior or contemporaneous agreements, understandings, and communications.

### 14.12 Amendments

Partiri reserves the right to modify these Terms at any time. For material changes, we will notify you by email or by prominent notice on the Services at least thirty (30) days before the changes take effect. Your continued use of the Services after the effective date of any changes constitutes your acceptance of the revised Terms. If you do not agree with the changes, you must stop using the Services before they take effect.

---

## 15. Contact Us

If you have any questions about these Terms or wish to submit a complaint, please contact us at:

**Partiri GmbH**
Email: legal@partiri.cloud
Website: partiri.cloud
